import { createSlice } from '@reduxjs/toolkit';

// Shared default shape for a brand-new custom role being built from scratch. Every
// module carries four independent booleans:
//   access  — can this role see/open the module at all (replaces the old "All X"/
//             "Custom X"/"None" picker)
//   create  — can this role add a new record
//   modify  — can this role edit an existing record
//   archive — can this role archive/delete a record
// (Properties/Setting only carry access/modify — "create a new field"/"create a new
// setting" aren't independent concepts, and archive doesn't apply to either. Reports
// carries only access — no modify/create/archive tier; which report *categories*
// are visible is the real granularity, see Reports.tabs / ReportCategoryAccess.
// ServiceRequest also only carries access/modify — requests are submitted by
// customers via wfc-customer, not created by staff; the only staff-side action is
// actioning one (push to schedule/mobile services, or reject).)
//
// UserRoles defaults to fully denied — unlike every other module here, which
// defaults to open — because it gates who can create/edit/archive *other* users and
// roles: a freshly-built custom role should have to be explicitly granted that, not
// inherit it by omission.
const defaultPropAccess = {
    Branch: {access: true, create: true, modify: true, archive: true},
    Employee: {access: true, create: true, modify: true, archive: true},
    Site: {access: true, create: true, modify: true, archive: true},
    Customer: {access: true, create: true, modify: true, archive: true},
    SiteGroup: {access: true, create: true, modify: true, archive: true},
    Properties: {access: true, modify: true},
    UserRoles: {access: false, create: false, modify: false, archive: false},
    Setting: {access: true, modify: true},
    Reports: {access: true},
    BusinessAnalysis: {access: true},
    Timesheet: {access: true, modify: true},
    ServiceRequest: {access: true, modify: true},
};

const permissionReducer = createSlice({
  name: 'permissionReducer',
  initialState: {
    propAccess: {...defaultPropAccess},
  },
  reducers: {
    // Merges a partial update ({access} / {create} / {modify} / {archive} / {tabs})
    // into one module's entry — used by every checkbox in the builder.
    updateModulePermission: (state, action)=>{
        const {objectType, ...rest} = action.payload;
        return{
            ...state,
            propAccess: {
                ...state.propAccess,
                [objectType] : {
                    ...state.propAccess[objectType],
                    ...rest
                }
            }
        }
    },
    // Toggles one tab's visibility for a module (Employee/Site/SiteGroup/Setting —
    // see src/util/types/tabPermission.types.js for the tab-key lists). Only an
    // explicit `0` hides a tab, so unchecking writes 0 and checking writes 1 rather
    // than deleting the key — keeps the stored shape predictable for review/diffing.
    updatePermissionTabs: (state, action) => {
        const {objectType, tabKey, visible} = action.payload;
        return {
            ...state,
            propAccess: {
                ...state.propAccess,
                [objectType]: {
                    ...state.propAccess[objectType],
                    tabs: {
                        ...state.propAccess[objectType]?.tabs,
                        [tabKey]: visible ? 1 : 0,
                    }
                }
            }
        }
    },
    resetPermission: (state, action)=>{
        return{
            ...state,
            propAccess: {...defaultPropAccess}
        }
    },
    // Loads an existing role's (or user's) saved permission wholesale — used when
    // editing a role/user, or when a role is picked in the "Choose user role" step.
    setPreDefinedDBPermission: (state, action) =>{
        return{
            ...state,
            propAccess: {...action.payload}
        }
    }
  },
});


export const { setPreDefinedDBPermission, resetPermission, updateModulePermission, updatePermissionTabs } = permissionReducer.actions;
export default permissionReducer.reducer;
